import { useState, useEffect, useRef } from 'react';

/**
 * Animates a number from its previous value to a new value.
 * Uses requestAnimationFrame for smooth ticking.
 */
export function useAnimatedNumber(target, duration = 800) {
  const [displayed, setDisplayed] = useState(target);
  const startRef    = useRef(target);
  const startTimeRef = useRef(null);
  const rafRef      = useRef(null);

  useEffect(() => {
    const from = startRef.current;
    const to   = target;
    if (from === to) return;

    startTimeRef.current = null;

    const step = (timestamp) => {
      if (!startTimeRef.current) startTimeRef.current = timestamp;
      const elapsed  = timestamp - startTimeRef.current;
      const progress = Math.min(elapsed / duration, 1);
      // Ease out cubic
      const eased    = 1 - Math.pow(1 - progress, 3);
      const value    = Math.round(from + (to - from) * eased);
      setDisplayed(value);

      if (progress < 1) {
        rafRef.current = requestAnimationFrame(step);
      } else {
        startRef.current = to;
      }
    };

    rafRef.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(rafRef.current);
  }, [target, duration]);

  return displayed;
}
